"use client";

import { useEffect, useState } from "react";

import Header from "./header";
import Footer from "./footer";
import JobLogs from "./job-logs";
import JobList, { type JobSummary } from "./job-list";
import { get_all, search } from "@/services/search-service";
import KeywordProfilesSidebar from "./keyword-profiles-sidebar";
import { useKeywordProfilesContext as useKeywordProfiles } from "@/context/keyword-profiles-context";

const STORAGE_KEY = "osint_job_id";

export function OsintDashboard() {
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobs, setJobs] = useState<JobSummary[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { profiles } = useKeywordProfiles();

  async function fetchJobs() {
    setIsLoading(true);
    try {
      const res = await get_all();
      setJobs(res);
    } finally {
      setIsLoading(false);
    }
  }

  // 🔥 restore jobId after reload
  useEffect(() => {
    fetchJobs();

    const savedJobId = localStorage.getItem(STORAGE_KEY);
    if (savedJobId) setJobId(savedJobId);
  }, []);

  const handleOnComplete = () => {
    fetchJobs();
    setJobId(null);
    localStorage.removeItem(STORAGE_KEY);
  }

  const handleOnApply = async () => {
    if (!profiles || profiles.length === 0) return;

    setIsLoading(true);
    try {
      const response = await search(profiles);
      setJobId(response.job_id);

      // 🔥 save jobId
      localStorage.setItem(STORAGE_KEY, response.job_id);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground overflow-hidden">
      {/* Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <div className="absolute inset-0 bg-linear-to-t from-background via-transparent to-transparent" />
        </div>
        <div className="scanline-overlay" />
      </div>

      {/* Content */}
      <div className="relative z-10">
        <Header />

        {/* MAIN LAYOUT */}
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-12 gap-6 pt-4">
          {/* SIDEBAR */}
          <aside className="col-span-12 md:col-span-3">
            <div className="sticky top-28 space-y-4">
              <KeywordProfilesSidebar
                onApply={handleOnApply}
                isLoading={isLoading}
              />
              <Footer />
            </div>
          </aside>

          {/* MAIN */}
          <main className="col-span-12 md:col-span-9 space-y-6">
            {jobId && (
              <JobLogs
                jobId={jobId}
                onJobDone={handleOnComplete}
              />
            )}

            <JobList jobs={jobs} isLoading={isLoading} />
          </main>
        </div>
      </div>
    </div>
  );
}
